
import { Product, UsageRecord } from "@/lib/types";
import StatsCard from "../StatsCard";
import { ArrowDownCircle, Package2, Clock, Calendar } from "lucide-react";
import { format } from "date-fns";
import { useState, useEffect } from "react";
import { calculateProductUsageRate } from "@/lib/storage/analytics/remote-data";
import { useToast } from "@/hooks/use-toast";

interface GroupStatsCardsProps {
  groupProducts: Product[];
  usageRecords: UsageRecord[];
  usageTimeRange: "1month" | "3months" | "6months";
  timeRange: "7days" | "30days" | "90days";
}

const GroupStatsCards = ({ 
  groupProducts, 
  usageRecords, 
  usageTimeRange, 
  timeRange 
}: GroupStatsCardsProps) => {
  const [groupUsageRate, setGroupUsageRate] = useState<number>(0);
  const [mostCriticalProduct, setMostCriticalProduct] = useState<{ product: Product, daysRemaining: number } | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { toast } = useToast();
  
  useEffect(() => {
    const fetchGroupUsageRates = async () => {
      if (groupProducts.length === 0) return;
      
      setIsLoading(true);
      try {
        const days = usageTimeRange === "1month" ? 30 : usageTimeRange === "3months" ? 90 : 180;
        
        // Calculate usage rate for each product in the group
        const productRatesPromises = groupProducts.map(async (product) => {
          const usageRate = await calculateProductUsageRate(product.id, days);
          const daysRemaining = usageRate > 0 ? Math.floor(product.currentStock / usageRate) : Infinity;
          
          return {
            product,
            usageRate,
            daysRemaining
          };
        });
        
        const productRates = await Promise.all(productRatesPromises);
        
        // Calculate total group usage rate
        const totalGroupUsageRate = productRates.reduce((sum, item) => sum + item.usageRate, 0);
        setGroupUsageRate(totalGroupUsageRate);
        
        // Find most critical product (least days remaining)
        const sortedByDaysRemaining = [...productRates].sort((a, b) => a.daysRemaining - b.daysRemaining);
        if (sortedByDaysRemaining.length > 0) {
          setMostCriticalProduct({
            product: sortedByDaysRemaining[0].product,
            daysRemaining: sortedByDaysRemaining[0].daysRemaining
          });
        }
        
        console.log(`Fetched group usage rate: ${totalGroupUsageRate.toFixed(2)} units/day`);
      } catch (error) {
        console.error("Error fetching group usage rates:", error);
        toast({
          title: "Error calculating group usage",
          description: "Could not retrieve the latest group statistics",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchGroupUsageRates();
  }, [groupProducts, usageTimeRange, toast]);
  
  // Calculate total group usage from the provided records
  const groupTotalUsage = usageRecords
    .filter(record => groupProducts.some(p => p.id === record.productId))
    .reduce((sum, record) => sum + record.quantity, 0);
  
  // Calculate total group current stock
  const groupTotalStock = groupProducts.reduce((sum, product) => sum + product.currentStock, 0);
  
  // Calculate days remaining for the group
  const groupDaysRemaining = mostCriticalProduct?.daysRemaining || 0;
  
  // Calculate restock date for the most critical product
  const restockDate = new Date();
  restockDate.setDate(restockDate.getDate() + groupDaysRemaining);

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
      <StatsCard
        title="Total Group Stock"
        value={groupTotalStock}
        icon={<Package2 className="h-5 w-5 text-blue-500 mr-2" />}
        badgeText="Units"
      />
      
      <StatsCard
        title={`Usage Rate (${usageTimeRange})`}
        value={isLoading ? "..." : groupUsageRate.toFixed(2)}
        icon={<Clock className="h-5 w-5 text-purple-500 mr-2" />}
        badgeText="Units/Day"
        badgeColor="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100"
      />
      
      <StatsCard
        title="Days Until Reorder"
        value={groupDaysRemaining}
        icon={<Calendar className="h-5 w-5 text-orange-500 mr-2" />}
        badgeText={groupDaysRemaining < (mostCriticalProduct?.product.minStockAlert || 7) ? "Low" : "Days"}
        badgeColor={groupDaysRemaining < (mostCriticalProduct?.product.minStockAlert || 7) 
          ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100" 
          : "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100"}
        subtitle={mostCriticalProduct ? `Restock by: ${format(restockDate, "MMM d, yyyy")}` : "No data"}
      />
      
      <StatsCard
        title={`${timeRange} Usage`}
        value={groupTotalUsage}
        icon={<ArrowDownCircle className="h-5 w-5 text-red-500 mr-2" />}
        badgeText="Used"
        badgeColor="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100"
      />
    </div>
  );
};

export default GroupStatsCards;
